/**
 * This program is free software; you can redistribute it and/or
 * modify it under the terms of the GNU General Public License
 * as published by the Free Software Foundation; under version 2
 * of the License (non-upgradable).
 *
 * This program is distributed in the hope that it will be useful,
 * but WITHOUT ANY WARRANTY; without even the implied warranty of
 * MERCHANTABILITY or FITNESS FOR A PARTICULAR PURPOSE.  See the
 * GNU General Public License for more details.
 *
 * You should have received a copy of the GNU General Public License
 * along with this program; if not, write to the Free Software
 * Foundation, Inc., 51 Franklin Street, Fifth Floor, Boston, MA  02110-1301, USA.
 *
 * Copyright (c) 2024 (original work) Open Assessment Technologies SA
 */

import { Userpilot } from 'userpilot';
import env from '@/config/env.js';
import { get } from 'svelte/store';
import { user } from '@/stores/auth.store.js';
import { currentTenantStore } from '@/stores/tenantConfiguration.store.js';
import { ROLE_ADMIN, ROLE_BACKOFFICE, ROLE_BATTERY, ROLE_CONTENT_CREATOR, ROLE_DASHBOARD_VIEWER, ROLE_DATA_EXPLORER, ROLE_GRADER, ROLE_GROUP_CREATOR, ROLE_GROUP_MANAGER, ROLE_OFFLINE_UPLOADER, ROLE_ONLINE_UPLOADER } from '@/constants/roles';
import { log } from '@/core/utils/logger';

const USERPILOT_TOKEN = env('USERPILOT_TOKEN');
const GA_TAG = env('GA_TAG');

const ROLES_WHITELIST = [
    ROLE_ADMIN,
    ROLE_BACKOFFICE,
    ROLE_BATTERY,
    ROLE_CONTENT_CREATOR,
    ROLE_GRADER,
    ROLE_GROUP_CREATOR,
    ROLE_GROUP_MANAGER,
    ROLE_OFFLINE_UPLOADER,
    ROLE_ONLINE_UPLOADER,
    ROLE_DASHBOARD_VIEWER,
    ROLE_DATA_EXPLORER
];

let gaMainScript = null;
let gaInitScript = null;

/**
 * Inserts Google Tag Manager main and initialization scripts to head
 * @param {string} id
 */
function activateGTM(id) {
    gaMainScript = document.createElement('script');
    gaMainScript.async = true;
    gaMainScript.src = `https://www.googletagmanager.com/gtag/js?id=${id}`;

    const range = document.createRange();
    range.selectNode(document.head);
    /* eslint-disable no-useless-escape */
    const text = `<script id="ga-init">
        window.dataLayer = window.dataLayer || [];
        function gtag(){dataLayer.push(arguments);}
        gtag('js', new Date());

        gtag('config', '${id}', {
            'environment': '${env('NODE_ENV') === 'production' ? 'Production' : 'Internal'}'
        });
    <\/script>`;
    /* eslint-enable no-useless-escape */
    gaInitScript = range.createContextualFragment(text);

    document.head.appendChild(gaMainScript);
    document.head.appendChild(gaInitScript);
}

/**
 * Initialize analytics for current user
 */
export function initialize() {
    if (window.SESSION_OVERRIDE) {
        return;
    }
    const currentUser = user.get();
    const currentTenant = get(currentTenantStore);
    // enable analytics for all users, except test-takers and users w/o role
    if (ROLES_WHITELIST.some(item => currentUser.roles.includes(item)) && USERPILOT_TOKEN) {
        log.debug('Initialize userpilot', { token: USERPILOT_TOKEN });
        if (USERPILOT_TOKEN) {
            Userpilot.initialize(USERPILOT_TOKEN);
            Userpilot.identify(`${currentTenant.tenantId}|${currentUser.login}`, {
                name: currentUser.name,
                login: currentUser.login,
                email: currentUser.email,
                roles: currentUser.roles,
                interfaceLanguage: currentUser.language,
                company: {
                    name: currentTenant.label,
                    id: currentTenant.tenantId
                }
            });
        }
        if (GA_TAG && !gaMainScript && !gaInitScript) {
            activateGTM(GA_TAG);
        }
    }
}

/**
 * Deactivate analytics
 */
export function deactivate() {
    Userpilot.destroy();
    if (gaMainScript || gaInitScript) {
        gaMainScript?.remove();
        document.querySelector('#ga-init')?.remove();
    }
}

/**
 * Trigger event
 * @param {string} name - event name
 */
export function triggerEvent(name) {
    Userpilot.track(name);
}
